import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';
import type { SystemActivityLog, SystemActivityFilters, SystemActivityType, SystemActivityCategory } from '../../../types/systemActivity';

export const withActivities = <TBase extends Constructor<any>>(Base: TBase) => class Activities extends Base {
  // ACTIVITY & COLLABORATION METHODS
  // ============================================================================

  /**
   * Get all activities
   */
  public getActivities(): Activity[] {
    const activities = Object.values(this.db.activities || {}) as Activity[];
    return activities;
  }

  /**
   * Get activity by ID
   */
  public getActivityById(id: string): Activity | undefined {
    return this.db.activities?.[id];
  }

  /**
   * Get activities by entity
   */
  public getActivitiesByEntity(entityType: string, entityId: string): Activity[] {
    return this.getActivities()
      .filter(a => a.related_to_type === entityType && a.related_to_id === entityId)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  /**
   * Get activities with filtering
   */
  public getActivitiesFiltered(filter: ActivityFilter): Activity[] {
    let activities = this.getActivities();

    // Filter by type
    if (filter.type && filter.type.length > 0) {
      activities = activities.filter(a => filter.type!.includes(a.type));
    }

    // Filter by related entity
    if (filter.related_to_type) {
      activities = activities.filter(a => a.related_to_type === filter.related_to_type);
    }
    if (filter.related_to_id) {
      activities = activities.filter(a => a.related_to_id === filter.related_to_id);
    }

    // Filter by creator
    if (filter.created_by_id) {
      activities = activities.filter(a => a.created_by_id === filter.created_by_id);
    }

    // Filter by date range
    if (filter.date_from) {
      activities = activities.filter(a => a.created_at >= filter.date_from!);
    }
    if (filter.date_to) {
      activities = activities.filter(a => a.created_at <= filter.date_to!);
    }

    // Filter by mentions
    if (filter.mentions_user_id) {
      activities = activities.filter(a => a.mentions.includes(filter.mentions_user_id!));
    }

    // Filter by pinned
    if (filter.is_pinned !== undefined) {
      activities = activities.filter(a => a.is_pinned === filter.is_pinned);
    }

    // Filter by follow-up required
    if (filter.requires_follow_up !== undefined) {
      activities = activities.filter(a => a.requires_follow_up === filter.requires_follow_up);
    }

    // Filter parent only (exclude replies)
    if (filter.parent_only) {
      activities = activities.filter(a => !a.parent_activity_id);
    }

    // Search query
    if (filter.search_query) {
      const query = filter.search_query.toLowerCase();
      activities = activities.filter(a =>
        (a.description || a.title || '').toLowerCase().includes(query) ||
        a.subject?.toLowerCase().includes(query) ||
        a.related_to_name.toLowerCase().includes(query) ||
        a.created_by_name.toLowerCase().includes(query)
      );
    }

    // Sort by created date (newest first)
    activities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return activities;
  }

  /**
   * Get activities for a specific project
   */
  public getProjectActivities(projectId: string): Activity[] {
    return this.getActivitiesFiltered({ related_to_type: 'project', related_to_id: projectId });
  }

  /**
   * Get activities for a specific client
   */
  public getClientActivities(clientId: string): Activity[] {
    return this.getActivitiesFiltered({ related_to_type: 'client', related_to_id: clientId });
  }

  /**
   * Get activities that mention a specific user
   */
  public getUserMentions(userId: string): Activity[] {
    return this.getActivitiesFiltered({ mentions_user_id: userId });
  }

  /**
   * Get activity replies (thread)
   */
  public getActivityReplies(parentActivityId: string): Activity[] {
    return this.getActivities().filter(a => a.parent_activity_id === parentActivityId)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  /**
   * Add a phone call activity
   */
  public addPhoneCall(data: CreatePhoneCallData, userId: string, userName: string, userEmail: string): Activity {
    const activity: Activity = {
      id: this.generateId(),
      type: 'phone_call',
      related_to_type: data.related_to_type,
      related_to_id: data.related_to_id,
      related_to_name: data.related_to_name,
      title: data.subject || `Phone call with ${data.contact_name}`,
      subject: data.subject || `Phone call with ${data.contact_name}`,
      description: data.notes,
      created_by_id: userId,
      created_by_name: userName,
      created_by_email: userEmail,
      created_at: new Date().toISOString(),
      call_duration_minutes: data.duration_minutes,
      call_contact_name: data.contact_name,
      call_contact_phone: data.contact_phone,
      call_outcome: data.outcome,
      kind: 'crm',
      source: 'human',
      mentions: data.mentions || [],
      replies_count: 0,
      is_internal: true,
      is_pinned: false,
      requires_follow_up: data.requires_follow_up,
      follow_up_date: data.follow_up_date,
      is_resolved: false,
      company_id: undefined,
      project_id: data.related_to_type === 'project' ? data.related_to_id : undefined,
      client_id: data.related_to_type === 'client' ? data.related_to_id : undefined,
    };

    this.db.activities[activity.id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * Add a meeting activity
   */
  public addMeeting(data: CreateMeetingData, userId: string, userName: string, userEmail: string): Activity {
    const activity: Activity = {
      id: this.generateId(),
      type: 'meeting',
      related_to_type: data.related_to_type,
      related_to_id: data.related_to_id,
      related_to_name: data.related_to_name,
      title: data.subject,
      subject: data.subject,
      description: data.notes,
      created_by_id: userId,
      created_by_name: userName,
      created_by_email: userEmail,
      created_at: new Date().toISOString(),
      meeting_start_time: data.start_time,
      meeting_end_time: data.end_time,
      meeting_location: data.location,
      meeting_attendees: data.attendees,
      meeting_type: data.meeting_type,
      kind: 'crm',
      source: 'human',
      mentions: data.mentions || [],
      replies_count: 0,
      is_internal: data.meeting_type === 'internal',
      is_pinned: false,
      requires_follow_up: data.requires_follow_up,
      follow_up_date: data.follow_up_date,
      is_resolved: false,
      company_id: undefined,
      project_id: data.related_to_type === 'project' ? data.related_to_id : undefined,
      client_id: data.related_to_type === 'client' ? data.related_to_id : undefined,
    };

    this.db.activities[activity.id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * Add an email activity
   */
  public addEmail(data: CreateEmailData, userId: string, userName: string, userEmail: string): Activity {
    const activity: Activity = {
      id: this.generateId(),
      type: 'email',
      related_to_type: data.related_to_type,
      related_to_id: data.related_to_id,
      related_to_name: data.related_to_name,
      title: data.subject,
      subject: data.subject,
      description: data.body,
      created_by_id: userId,
      created_by_name: userName,
      created_by_email: userEmail,
      created_at: new Date().toISOString(),
      email_to: data.to,
      email_cc: data.cc,
      email_subject: data.subject,
      email_sent_at: data.sent_at,
      kind: 'crm',
      source: 'human',
      mentions: data.mentions || [],
      replies_count: 0,
      is_internal: false,
      is_pinned: false,
      requires_follow_up: false,
      is_resolved: false,
      company_id: undefined,
      project_id: data.related_to_type === 'project' ? data.related_to_id : undefined,
      client_id: data.related_to_type === 'client' ? data.related_to_id : undefined,
    };

    this.db.activities[activity.id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * Add a note activity
   */
  public addNote(data: CreateNoteData, userId: string, userName: string, userEmail: string): Activity {
    const activity: Activity = {
      id: this.generateId(),
      type: 'note',
      related_to_type: data.related_to_type,
      related_to_id: data.related_to_id,
      related_to_name: data.related_to_name,
      title: data.subject || 'Note',
      subject: data.subject,
      description: data.description,
      created_by_id: userId,
      created_by_name: userName,
      created_by_email: userEmail,
      created_at: new Date().toISOString(),
      kind: 'crm',
      source: 'human',
      mentions: data.mentions || [],
      replies_count: 0,
      is_internal: data.is_internal,
      is_pinned: false,
      requires_follow_up: false,
      is_resolved: false,
      company_id: undefined,
      project_id: data.related_to_type === 'project' ? data.related_to_id : undefined,
      client_id: data.related_to_type === 'client' ? data.related_to_id : undefined,
    };

    this.db.activities[activity.id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * Add a comment/reply activity
   */
  public addComment(data: CreateCommentData, userId: string, userName: string, userEmail: string): Activity {
    const activity: Activity = {
      id: this.generateId(),
      type: 'comment',
      related_to_type: data.related_to_type,
      related_to_id: data.related_to_id,
      related_to_name: data.related_to_name,
      title: 'Comment',
      description: data.description,
      created_by_id: userId,
      created_by_name: userName,
      created_by_email: userEmail,
      created_at: new Date().toISOString(),
      parent_activity_id: data.parent_activity_id || null,
      kind: 'crm',
      source: 'human',
      mentions: data.mentions || [],
      replies_count: 0,
      is_internal: true,
      is_pinned: false,
      requires_follow_up: false,
      is_resolved: false,
      company_id: undefined,
      project_id: data.related_to_type === 'project' ? data.related_to_id : undefined,
      client_id: data.related_to_type === 'client' ? data.related_to_id : undefined,
    };

    // If this is a reply, increment parent's reply count
    if (data.parent_activity_id) {
      const parent = this.db.activities[data.parent_activity_id];
      if (parent) {
        parent.replies_count++;
      }
    }

    this.db.activities[activity.id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * NOTE: Activities are IMMUTABLE for audit compliance
   * 
   * Once created, activities cannot be edited to ensure:
   * - Complete audit trail integrity
   * - RICS compliance requirements
   * - Legal evidence preservation
   * - Prevention of record tampering
   * 
   * Activities can only be:
   * - Created (logged)
   * - Deleted (by creator only)
   * - Pinned/unpinned (metadata only)
   * - Marked as resolved (metadata only)
   * 
   * The core content (subject, content, timestamps) is permanent.
   */

  /**
   * Pin/unpin an activity
   */
  public toggleActivityPin(id: string): Activity | null {
    const activity = this.db.activities?.[id];
    if (!activity) return null;

    activity.is_pinned = !activity.is_pinned;
    this.db.activities[id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * Mark activity as resolved
   */
  public resolveActivity(id: string): Activity | null {
    const activity = this.db.activities?.[id];
    if (!activity) return null;

    activity.is_resolved = true;
    this.db.activities[id] = activity;
    this.saveToStorage();
    this.notifyListeners();
    return activity;
  }

  /**
   * Generic activity logging method for system events
   * Use this to log any project changes, document actions, etc.
   */
  public logActivity(data: {
    type: string;
    related_to_type?: string;
    related_to_id?: string;
    related_to_name?: string;
    title: string;
    description?: string;
    subject?: string;
    content?: string;
    category?: string;
    action?: string;
    detail?: string;
    user_id?: string;
    user_name?: string;
    user_email?: string;
    userId?: string;
    userName?: string;
    userEmail?: string;
    projectId?: string;
    projectName?: string;
    clientId?: string;
    clientName?: string;
    companyId?: string;
    companyName?: string;
    document_id?: string;
    document_name?: string;
    document_size?: number;
    document_category?: string;
    change_type?: string;
    change_field?: string;
    change_old_value?: string;
    change_new_value?: string;
    amount?: number;
    currency?: string;
    status_from?: string;
    status_to?: string;
    status_reason?: string;
    file_url?: string;
    file_name?: string;
    file_type?: string;
    file_size?: number;
    is_internal?: boolean;
    mentions?: string[];
    metadata?: Record<string, any>;
  }): Activity {
    const metadata = data.metadata || {};
    const normalizedType =
      data.type === 'project_status_changed'
        ? 'status_update'
        : data.type === 'project_updated'
          ? 'project_update'
          : data.type;

    const relatedToType =
      data.related_to_type ||
      (data.projectId || metadata.projectId
        ? 'project'
        : data.clientId || metadata.clientId
          ? 'client'
          : data.companyId || metadata.companyId
            ? 'company'
            : 'project');
    const relatedToId =
      data.related_to_id ||
      data.projectId ||
      metadata.projectId ||
      data.clientId ||
      metadata.clientId ||
      data.companyId ||
      metadata.companyId ||
      'unknown';
    const relatedToName =
      data.related_to_name ||
      data.projectName ||
      metadata.projectName ||
      data.clientName ||
      metadata.clientName ||
      data.companyName ||
      metadata.companyName ||
      'Unknown';
    const createdById = data.user_id || data.userId || 'system';
    const createdByName = data.user_name || data.userName || 'System';
    const createdByEmail = data.user_email || data.userEmail || '';
    const title = data.title || data.subject || data.action || '';
    const description = data.description || data.detail || data.content;
    const statusFrom = data.status_from || metadata.previousValue;
    const statusTo = data.status_to || metadata.newValue;
    const amount =
      data.amount !== undefined
        ? data.amount
        : metadata.contractValue !== undefined
          ? metadata.contractValue
          : undefined;

    const activity: Activity = {
      id: this.generateId(),
      type: normalizedType as any,
      related_to_type: relatedToType as any,
      related_to_id: relatedToId,
      related_to_name: relatedToName,
      title,
      description,
      created_by_id: createdById,
      created_by_name: createdByName,
      created_by_email: createdByEmail,
      created_at: new Date().toISOString(),
      document_id: data.document_id,
      document_name: data.document_name,
      document_size: data.document_size,
      document_category: data.document_category,
      change_type: data.change_type,
      change_field: data.change_field,
      change_old_value: data.change_old_value,
      change_new_value: data.change_new_value,
      amount: data.amount,
      currency: data.currency || 'GBP',
      status_from: statusFrom,
      status_to: statusTo,
      status_reason: data.status_reason,
      file_url: data.file_url,
      file_name: data.file_name,
      file_type: data.file_type,
      file_size: data.file_size,
      mentions: data.mentions || [],
      replies_count: 0,
      is_internal: data.is_internal !== undefined ? data.is_internal : true,
      is_pinned: false,
      requires_follow_up: false,
      is_resolved: false,
      project_id: relatedToType === 'project' ? relatedToId : undefined,
      client_id: relatedToType === 'client' ? relatedToId : undefined,
      kind: 'system',
      source: 'system',
    };

    if (!this.db.activities) {
      this.db.activities = {};
    }

    this.db.activities[activity.id] = activity;
    this.saveToLocalStorage();

    setTimeout(() => {
      this.saveEntityToBackend('activities', activity.id, activity, true);
    }, 2000);

    this.notifyListeners();
    return activity;
  }

  /**
   * Delete an activity
   */
  public deleteActivity(id: string): boolean {
    if (!this.db.activities?.[id]) return false;

    // Also delete all replies to this activity
    const replies = this.getActivityReplies(id);
    replies.forEach(reply => {
      delete this.db.activities[reply.id];
    });

    delete this.db.activities[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  /**
   * Get activity statistics
   */
  public getActivityStats(userId?: string): ActivityStats {
    let activities = this.getActivities();

    // Filter by user if specified
    if (userId) {
      activities = activities.filter(a => a.created_by_id === userId);
    }

    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const activitiesThisWeek = activities.filter(a => new Date(a.created_at) >= oneWeekAgo).length;
    const activitiesThisMonth = activities.filter(a => new Date(a.created_at) >= oneMonthAgo).length;

    // Count by type
    const byType = activities.reduce((acc, a) => {
      acc[a.type] = (acc[a.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Count by project
    const byProjectMap = new Map<string, { name: string; count: number }>();
    activities.filter(a => a.project_id).forEach(a => {
      if (!a.project_id) return;
      const existing = byProjectMap.get(a.project_id);
      if (existing) {
        existing.count++;
      } else {
        byProjectMap.set(a.project_id, { name: a.related_to_name, count: 1 });
      }
    });
    const byProject = Array.from(byProjectMap.entries()).map(([project_id, data]) => ({
      project_id,
      project_name: data.name,
      count: data.count,
    }));

    // Count by user
    const byUserMap = new Map<string, { name: string; count: number }>();
    activities.forEach(a => {
      const existing = byUserMap.get(a.created_by_id);
      if (existing) {
        existing.count++;
      } else {
        byUserMap.set(a.created_by_id, { name: a.created_by_name, count: 1 });
      }
    });
    const byUser = Array.from(byUserMap.entries()).map(([user_id, data]) => ({
      user_id,
      user_name: data.name,
      count: data.count,
    }));

    const pendingFollowUps = activities.filter(a => a.requires_follow_up && !a.is_resolved).length;
    const mentionsForUser = userId ? this.getUserMentions(userId).length : 0;

    return {
      total_activities: activities.length,
      activities_this_week: activitiesThisWeek,
      activities_this_month: activitiesThisMonth,
      by_type: byType as Record<string, number>,
      by_project: byProject,
      by_user: byUser,
      pending_follow_ups: pendingFollowUps,
      mentions_for_user: mentionsForUser,
    };
  }

  // ============================================================================

  // SYSTEM ACTIVITY LOGS
  // ============================================================================

  /**
   * Log a system activity (immutable audit trail)
   */
  public logSystemActivity(params: {
    type: SystemActivityType;
    category: SystemActivityCategory;
    action: string;
    detail: string;
    userId: string;
    userName: string;
    userEmail: string;
    metadata?: any;
  }): void {
    const id = crypto.randomUUID();
    const log: SystemActivityLog = {
      id,
      type: params.type,
      category: params.category,
      action: params.action,
      detail: params.detail,
      userId: params.userId,
      userName: params.userName,
      userEmail: params.userEmail,
      timestamp: new Date().toISOString(),
      metadata: params.metadata,
      immutable: true,
    };

    if (!this.db.systemActivityLogs) {
      this.db.systemActivityLogs = {};
    }
    
    this.db.systemActivityLogs[id] = log;
    this.saveToStorage();
    // Don't notify listeners for activity logs to avoid performance issues
  }

  /**
   * Get system activity logs with optional filters
   */
  public getSystemActivityLogs(filters?: SystemActivityFilters): SystemActivityLog[] {
    if (!this.db.systemActivityLogs) {
      return [];
    }

    let logs: SystemActivityLog[] = Object.values(this.db.systemActivityLogs) as SystemActivityLog[];

    // Apply filters
    if (filters) {
      if (filters.category) {
        logs = logs.filter((log: SystemActivityLog) => log.category === filters.category);
      }
      if (filters.type) {
        logs = logs.filter((log: SystemActivityLog) => log.type === filters.type);
      }
      if (filters.userId) {
        logs = logs.filter((log: SystemActivityLog) => log.userId === filters.userId);
      }
      if (filters.startDate) {
        logs = logs.filter((log: SystemActivityLog) => log.timestamp >= filters.startDate!);
      }
      if (filters.endDate) {
        logs = logs.filter((log: SystemActivityLog) => log.timestamp <= filters.endDate!);
      }
    }

    // Sort by timestamp descending (most recent first)
    logs.sort((a: SystemActivityLog, b: SystemActivityLog) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Apply limit
    if (filters?.limit) {
      logs = logs.slice(0, filters.limit);
    }

    return logs;
  }

  /**
   * Get recent system activity (last 50 by default)
   */
  public getRecentSystemActivity(limit: number = 50): SystemActivityLog[] {
    return this.getSystemActivityLogs({ limit });
  }

  /**
   * Seed initial activity logs for demonstration (call once on system init if no logs exist)
   */
  public seedInitialActivityLogs(): void {
    // Only seed if no logs exist
    if (this.db.systemActivityLogs && Object.keys(this.db.systemActivityLogs).length > 0) {
      return;
    }

    const now = new Date();
    const systemUser = {
      id: 'system',
      name: 'System',
      email: 'system@contractgroup.co.uk',
    };

    // Sample activities from recent to older
    const sampleActivities = [
      {
        type: 'user_login' as const,
        category: 'user' as const,
        action: 'User logged in',
        detail: 'gary@contractgroup.co.uk',
        timestamp: new Date(now.getTime() - 30 * 60000).toISOString(), // 30 mins ago
      },
      {
        type: 'project_updated' as const,
        category: 'project' as const,
        action: 'Project updated',
        detail: 'Budget revised for Q1 Development',
        timestamp: new Date(now.getTime() - 2 * 3600000).toISOString(), // 2 hours ago
      },
      {
        type: 'company_updated' as const,
        category: 'company' as const,
        action: 'Company details updated',
        detail: 'Updated billing information',
        timestamp: new Date(now.getTime() - 5 * 3600000).toISOString(), // 5 hours ago
      },
      {
        type: 'system_backup' as const,
        category: 'system' as const,
        action: 'System backup completed',
        detail: 'All data backed up successfully',
        timestamp: new Date(now.getTime() - 24 * 3600000).toISOString(), // 1 day ago
      },
      {
        type: 'integration_connected' as const,
        category: 'integration' as const,
        action: 'Integration connected',
        detail: 'Google Drive integration activated',
        timestamp: new Date(now.getTime() - 2 * 24 * 3600000).toISOString(), // 2 days ago
      },
    ];

    sampleActivities.forEach((activity, index) => {
      const id = `activity_seed_${Date.now()}_${index}`;
      const log: SystemActivityLog = {
        id,
        type: activity.type,
        category: activity.category,
        action: activity.action,
        detail: activity.detail,
        userId: systemUser.id,
        userName: systemUser.name,
        userEmail: systemUser.email,
        timestamp: activity.timestamp,
        immutable: true,
      };

      if (!this.db.systemActivityLogs) {
        this.db.systemActivityLogs = {};
      }
      
      this.db.systemActivityLogs[id] = log;
    });

    this.saveToStorage();
  }
};
